import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const includes = [
  'Pre-shoot strategy & concept development',
  'Creative direction & shot list',
  'On-site content capture (photo + video)',
  'Real-time creative direction',
  'Content optimized for social & campaigns',
];

const addOns = [
  'Professional makeup artist',
  'Full styling service',
  'Model sourcing',
  'Location scouting',
  'Same-day content previews',
];

export default function VipContentDaysPage() {
  return (
    <div className="pt-44" data-testid="vip-page">
      {/* Hero */}
      <section className="py-16 md:py-24" data-testid="vip-hero">
        <div className="container-main">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-butter font-semibold mb-6">
              A Muse Experience
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-8">
              Your Brand, Captured With Intention.
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              A fully curated, high-touch content experience designed to create campaign-ready assets in a single session.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Includes */}
      <section className="pt-0 pb-24 md:pb-36" data-testid="vip-includes">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="max-w-3xl mx-auto"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-8">
                  Includes
                </motion.p>
                <motion.div variants={fadeInUp} className="space-y-5">
                  {includes.map((item, i) => (
                    <div key={i} className="flex items-start gap-4" data-testid={`vip-include-${i}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-butter flex-shrink-0 mt-2" />
                      <span className="text-foreground text-base leading-relaxed">{item}</span>
                    </div>
                  ))}
                </motion.div>
              </div>

              <div>
                <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-8">
                  Optional Add-ons
                </motion.p>
                <motion.div variants={fadeInUp} className="space-y-5">
                  {addOns.map((item, i) => (
                    <div key={i} className="flex items-start gap-4" data-testid={`vip-addon-${i}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-border flex-shrink-0 mt-2" />
                      <span className="text-muted-foreground text-base leading-relaxed">{item}</span>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>

            <motion.div variants={fadeInUp} className="mt-20 flex justify-center">
              <img
                src="/images/be-the-muse.png"
                alt="Be the Muse. We take care of the rest."
                className="w-full max-w-sm h-auto"
                data-testid="vip-be-the-muse-image"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-secondary" data-testid="vip-cta">
        <div className="container-main text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-6">
              Ready for your VIP experience?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg mb-10 max-w-xl mx-auto">
              Tell us about your brand and vision. We'll handle the rest.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link
                to="/book-a-call"
                className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300"
                data-testid="vip-book-btn"
              >
                Book a VIP Experience <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
